/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      // Clear preselected state: uncheck inputs and reset dropdown buttons
      this.optionSelectors.forEach(selector => {
        if (selector.dataset.selectorType === 'dropdown') {
          const btn = selector.querySelector('.custom-select__btn');
          if (btn) btn.textContent = '';
        } else {
          selector.querySelectorAll('input:checked').forEach(input => {
            input.checked = false;
          });
        }
      });

      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : unavailableStr;
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;

      // Always start with JSON product variants
      let variants = this.data.product.variants;

      // Try noscript override if present
      const noscript = document.getElementById('variantOptionsData');
      if (noscript) {
        const parsedDoc = new DOMParser().parseFromString(noscript.innerHTML, 'text/html');
        const parsedVariants = Array.from(parsedDoc.querySelectorAll('.variant-options-data')).map(div => {
          const data = JSON.parse(div.getAttribute('data-variant-options'));
          return {
            id: data.id,
            options: data.options.split('/'),
            available: data.available ?? true
          };
        });

        // Only trust noscript if it includes availability
        if (parsedVariants.length && parsedVariants.some(v => v.available !== undefined)) {
          variants = parsedVariants;
        } else {
          console.warn("NOSCRIPT variants missing availability, falling back to JSON product data");
        }
      }

      const selectedOptions = this.getSelectedOptions();
      const optionNames = this.data.product.options;

      const toggleOption = (selector, validValues, soldOutValues) => {
        selector.querySelectorAll('.js-option').forEach((el) => {
          const val = el.value || el.dataset.value;
          const label = selector.querySelector(`label[for="${el.id}"]`);

          if (!validValues.includes(val)) {
            if (label) label.classList.add('hidden');
            el.classList.add('is-unavailable');
            el.disabled = true;
          } else {
            if (label) label.classList.remove('hidden');
            el.disabled = false;

            if (soldOutValues.includes(val)) {
              el.classList.add('is-unavailable');
            } else {
              el.classList.remove('is-unavailable');
            }
          }
        });
      };

      // Walk through each option group
      this.optionSelectors.forEach((selector, optionIndex) => {
        const prevSelections = selectedOptions.slice(0, optionIndex);

        const matchingVariants = variants.filter(v =>
          prevSelections.every((sel, idx) => sel ? v.options[idx] === sel : true)
        );

        const validValues = [...new Set(matchingVariants.map(v => v.options[optionIndex]))];
        const soldOutValues = validValues.filter(val =>
          matchingVariants
            .filter(v => v.options[optionIndex] === val)
            .every(v => !v.available)
        );

        toggleOption(selector, validValues, soldOutValues);
        
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant;
      this.price.querySelector('.price__no-variant').hidden = this.variant;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        // Ensure quantity input resets/adjusts when variant changes
        const qtyInput = form.querySelector('input[name="quantity"]');
        if (qtyInput) {
          // If a minimum quantity modifier is provided via data attribute, use it; otherwise default to 1
          const minQty = parseInt(qtyInput.dataset.qtyModify || qtyInput.getAttribute('min') || 1, 10) || 1;
          // Reset only if current value is greater than available or to ensure expected baseline
          qtyInput.value = minQty;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */

    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const btn = selector.querySelector('.custom-select__btn');
          selectedOptions.push(btn ? btn.textContent.trim() : null);
        } else {
          const checkedInput = selector.querySelector('input:checked');
          selectedOptions.push(checkedInput ? checkedInput.value : null);
        }
      });

      return selectedOptions;
    }


    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }


  customElements.define('variant-picker', VariantPicker);
}

  /* Variant Popup in PDP */
  document.addEventListener("DOMContentLoaded", () => {
    // Elements
    const openBtn = document.getElementById("variant-picker-anchor");
    const modal = document.getElementById("variant-modal");
    const closeBtn = document.getElementById("close-variant-modal");
    const confirmBtn = document.getElementById("confirm-variant");
    const header = document.querySelector(".cc-header");
    const mainContent = document.getElementById("main-content");

    if (!modal || !openBtn || !closeBtn || !confirmBtn) return;

    const variantsPicker = modal.querySelector("variant-picker");
    if (!variantsPicker) return;

    // Initially disable confirm button
    confirmBtn.disabled = true;
    confirmBtn.classList.add("disabled");

    // Function to check if all options are selected
    function checkAllOptionsSelected() {
      const selectedOptions = variantsPicker.getSelectedOptions(); // e.g. ['King', 'Medium', null]
      const allSelected = selectedOptions.every(option => option !== null);

      confirmBtn.disabled = !allSelected;
      confirmBtn.classList.toggle("disabled", !allSelected);
    }

    // Open modal
    function openModal() {
      modal.style.display = "flex";
      document.body.classList.add("no-scroll");

      // Lower header and main content
      if (header) header.classList.add("lowered");
      if (mainContent) mainContent.classList.add("lowered");

      checkAllOptionsSelected(); // ensure Confirm button is correct
    }

    // Close modal
    function closeModal() {
      modal.style.display = "none";
      document.body.classList.remove("no-scroll");

      if (header) header.classList.remove("lowered");
      if (mainContent) mainContent.classList.remove("lowered");
    }

    // Event listeners
    openBtn.addEventListener("click", openModal);
    closeBtn.addEventListener("click", closeModal);

    // Close modal if clicking outside content
    window.addEventListener("click", (e) => {
      if (e.target === modal) closeModal();
    });

    // Listen for changes inside variant-picker
    variantsPicker.addEventListener("change", checkAllOptionsSelected);

    // Confirm button click
    confirmBtn.addEventListener("click", () => {
      const selectedOptions = variantsPicker.getSelectedOptions();
      if (selectedOptions.includes(null)) return; // safety check

      // Format display text: Option1 | Option2 x Option3
      let displayText = selectedOptions[0];
      if (selectedOptions.length > 1) {
        displayText += " | " + selectedOptions.slice(1).join(" x ");
      }

      // Update main button
      openBtn.innerHTML = `<span class="product-options__option">${displayText}</span>`;

      // Close modal
      closeModal();
    });
  });
